import React, { useState, useEffect } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle, Sparkles, Timer, HelpCircle, HeartHandshake, Zap } from 'lucide-react';
import { toast } from 'sonner';

interface StretchingTrackerProps {
  selectedDate: string;
  clientId?: number; // When set, show client's data (trainer view — read-only)
}

interface StretchItem {
  id: string;
  name: string;
  targetArea: string;
  duration: string;
  completed: boolean;
  categoryLink?: string;
}

const DEFAULT_STRETCHES: StretchItem[] = [
  { id: 'stretch-1', name: 'Couch Stretch', targetArea: 'Hip Flexors & Quads', duration: '2 mins per side', completed: false, categoryLink: 'Legs' },
  { id: 'stretch-2', name: 'Pigeon Pose', targetArea: 'Glutes & Hips', duration: '2 mins per side', completed: false, categoryLink: 'Legs' },
  { id: 'stretch-3', name: "World's Greatest Stretch", targetArea: 'Full Body & Thoracic Spine', duration: '5 reps per side', completed: false },
  { id: 'stretch-4', name: 'Hamstring Band Stretch', targetArea: 'Hamstrings', duration: '90s per side', completed: false, categoryLink: 'Legs' },
  { id: 'stretch-5', name: 'Chest/Shoulder Doorway Stretch', targetArea: 'Chest & Anterior Delts', duration: '1 min', completed: false, categoryLink: 'Chest' },
  { id: 'stretch-6', name: 'Lat Foam Roll & Stretch', targetArea: 'Lats & Upper Back', duration: '2 mins', completed: false, categoryLink: 'Back' },
  { id: 'stretch-7', name: 'Thread the Needle', targetArea: 'Shoulders & Thoracic', duration: '1 min per side', completed: false, categoryLink: 'Shoulders' },
];

export default function StretchingTracker({ selectedDate, clientId }: StretchingTrackerProps) {
  const isClientView = !!clientId;
  const [stretches, setStretches] = useState<StretchItem[]>([]);
  const [workedCategories, setWorkedCategories] = useState<string[]>([]);

  const utils = trpc.useUtils();

  // Get sessions for today to detect worked categories
  const { data: rawSessions = [] } = trpc.sessions.byDate.useQuery({ date: selectedDate }, { enabled: !isClientView });
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();

  // Get stretching log from server
  const { data: ownStretchLog } = trpc.stretching.byDate.useQuery({ date: selectedDate }, { enabled: !isClientView });
  const { data: clientStretchLog } = trpc.trainerPortal.getClientStretchingLog.useQuery(
    { clientId: clientId!, date: selectedDate },
    { enabled: isClientView && !!clientId }
  );
  const stretchLog = isClientView ? clientStretchLog : ownStretchLog;

  const saveStretchingMutation = trpc.stretching.save.useMutation({
    onSuccess: () => utils.stretching.byDate.invalidate({ date: selectedDate }),
  });

  // Detect worked categories from today's session
  useEffect(() => {
    const categories: string[] = [];
    rawSessions.forEach((session) => {
      const plannedExercises = (session.plannedExercises as any[]) || [];
      plannedExercises.forEach((pe: any) => {
        const ex = rawExercises.find((e) => e.id === pe.exerciseId);
        if (ex && !categories.includes(ex.category)) {
          categories.push(ex.category);
        }
      });
    });
    setWorkedCategories(categories);
  }, [rawSessions, rawExercises]);

  // Load stretches from server or use defaults
  useEffect(() => {
    if (stretchLog?.completedStretches) {
      try {
        const parsed = typeof stretchLog.completedStretches === 'string' ? JSON.parse(stretchLog.completedStretches as string) : stretchLog.completedStretches;
        setStretches(parsed as StretchItem[]);
      } catch (e) {
        setStretches(DEFAULT_STRETCHES);
      }
    } else {
      setStretches(DEFAULT_STRETCHES);
    }
  }, [stretchLog]);

  const saveStretches = async (updated: StretchItem[]) => {
    setStretches(updated);
    await saveStretchingMutation.mutateAsync({
      date: selectedDate,
      completedStretches: updated,
    });
  };

  const handleToggleStretch = async (id: string) => {
    let completedState = false;
    const updated = stretches.map((s) => {
      if (s.id === id) {
        completedState = !s.completed;
        return { ...s, completed: completedState };
      }
      return s;
    });

    await saveStretches(updated);

    if (completedState) {
      toast.success('Mobility logged! Your joints will thank you.', {
        icon: <HeartHandshake className="h-4 w-4 text-success" />,
      });
    }
  };

  const completedCount = stretches.filter((s) => s.completed).length;
  const progressPercent = stretches.length > 0 ? Math.round((completedCount / stretches.length) * 100) : 0;

  const recommendedStretches = stretches.filter((s) => s.categoryLink && workedCategories.includes(s.categoryLink));
  const otherStretches = stretches.filter((s) => !s.categoryLink || !workedCategories.includes(s.categoryLink));

  return (
    <div className="forge-panel p-4 space-y-4">
      <div className="forge-header">
        <span>Flexibility & Mobility</span>
        <span className="text-primary font-mono flex items-center gap-1">
          <Sparkles className="h-3.5 w-3.5" /> MOBILITY
        </span>
      </div>

      <div className="space-y-4">
        {workedCategories.length > 0 && (
          <div className="bg-primary/5 border border-primary/20 p-2.5 rounded-sm flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-mono text-primary font-bold uppercase flex items-center gap-1 shrink-0">
              <Zap className="h-3.5 w-3.5 text-primary" /> Muscles Trained Today:
            </span>
            <div className="flex flex-wrap gap-1">
              {workedCategories.map((cat) => (
                <Badge key={cat} className="bg-primary/20 text-primary border border-primary/30 font-mono text-[9px] uppercase">
                  {cat}
                </Badge>
              ))}
            </div>
          </div>
        )}

        <div className="bg-secondary/40 border border-border/60 rounded-sm p-3 space-y-2">
          <div className="flex justify-between text-xs font-mono uppercase text-muted-foreground">
            <span>Daily Progress</span>
            <span className="text-primary font-bold">
              {completedCount} / {stretches.length} COMPLETED
            </span>
          </div>
          <div className="w-full bg-background/60 h-2 rounded-full overflow-hidden border border-border/40">
            <div className="bg-primary h-full transition-all duration-500 forge-glow-green" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className="space-y-3">
          {recommendedStretches.length > 0 && (
            <div className="space-y-1.5">
              <span className="block text-[9px] font-mono uppercase text-primary font-bold tracking-wider">★ TARGETED MOBILITY RECOMMENDATIONS FOR TODAY</span>
              <div className="space-y-1.5">
                {recommendedStretches.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => !isClientView && handleToggleStretch(s.id)}
                    className={`p-3 border rounded-sm flex items-center justify-between transition-all ${isClientView ? 'cursor-default' : 'cursor-pointer'} ${s.completed ? 'bg-success/5 border-success/30 hover:border-success/50' : 'bg-primary/5 border-primary/30 hover:border-primary/50'}`}
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <button className="mt-0.5 shrink-0">
                        {s.completed ? <CheckCircle2 className="h-4 w-4 text-success forge-glow-green" /> : <Circle className="h-4 w-4 text-primary/60" />}
                      </button>
                      <div className="min-w-0">
                        <h4 className={`font-bold text-sm ${s.completed ? 'text-success/90 line-through' : 'text-foreground'}`}>{s.name}</h4>
                        <div className="flex flex-wrap items-center gap-1.5 mt-1">
                          <Badge className="bg-primary/20 text-primary border border-primary/30 font-mono text-[9px] uppercase">{s.targetArea}</Badge>
                          <span className="text-[9px] font-mono text-primary flex items-center gap-0.5">
                            <Timer className="h-3 w-3" /> {s.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            {recommendedStretches.length > 0 && <span className="block text-[9px] font-mono uppercase text-muted-foreground tracking-wider">GENERAL MOBILITY ROUTINES</span>}
            <div className="space-y-1.5">
              {otherStretches.map((s) => (
                <div
                  key={s.id}
                  onClick={() => !isClientView && handleToggleStretch(s.id)}
                  className={`p-3 border rounded-sm flex items-center justify-between transition-all ${isClientView ? 'cursor-default' : 'cursor-pointer'} ${s.completed ? 'bg-success/5 border-success/30 hover:border-success/50' : 'bg-secondary/20 border-border/60 hover:border-primary/40'}`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <button className="mt-0.5 shrink-0">
                      {s.completed ? <CheckCircle2 className="h-4 w-4 text-success forge-glow-green" /> : <Circle className="h-4 w-4 text-muted-foreground/60" />}
                    </button>
                    <div className="min-w-0">
                      <h4 className={`font-bold text-sm ${s.completed ? 'text-success/90 line-through' : 'text-foreground'}`}>{s.name}</h4>
                      <div className="flex flex-wrap items-center gap-1.5 mt-1">
                        <Badge variant="outline" className="font-mono text-[9px] bg-background/50 border-border text-muted-foreground uppercase">
                          {s.targetArea}
                        </Badge>
                        <span className="text-[9px] font-mono text-primary flex items-center gap-0.5">
                          <Timer className="h-3 w-3" /> {s.duration}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-background/40 border border-border/40 p-2.5 rounded-sm flex items-start gap-2 text-[10px] font-mono uppercase text-muted-foreground">
          <HelpCircle className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <p className="leading-relaxed">Ross, try targeting couch stretches and hip openers right after golf or a heavy leg session to maintain lower body rotation.</p>
        </div>
      </div>
    </div>
  );
}
